import { CloseFullscreenRounded } from '@mui/icons-material'
import { IconButton, Stack, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridPaginationMeta, GridPaginationModel } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IPaginationQueryResponse } from '../../definitions/Common'
import { ICostUtilization } from '../../definitions/DataAnalytics'
import { ViewType } from '../contract_tabs/NewContractAnalytics'
import SmartCard from '../controls/cards/SmartCard'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import { snackbar } from '../services/SnackbarService'

interface P {
    contractId: number
    filterStartDate?: string
    filterEndDate?: string
    changeViewFunction: (view: ViewType) => void
    updateDataFlag: boolean
    setExportMenu: (items: string[]) => void

}

export const valueFormatter = (unit: 'percent' | 'currency', value: number) => {
    return new Intl.NumberFormat('en-US', { style: unit, currency: 'USD' }).format(
        value
    )
}

const columns: GridColDef<ICostUtilization>[] = [
    { field: 'practiceGroup', headerName: 'Practice Group', flex: 1.5 },
    { field: 'horizon', headerName: 'Horizon', flex: 1 },
    { field: 'ageGroup', headerName: 'Age Group', flex: 1 },
    { field: 'tosLevelOne', headerName: 'TOS Level 1', flex: 1 },
    { field: 'tosLevelTwo', headerName: 'TOS Level 2', flex: 1, renderCell: params => params?.value ?? 'missing' },
    { field: 'tosLevelThree', headerName: 'TOS Level 3', flex: 1, renderCell: params => params?.value ?? 'missing' },
    { field: 'entityTCOC', headerName: 'Entity TCOC', flex: 1, type: 'number', renderCell: params => valueFormatter('currency', params.value) },
    { field: 'entityPMPMUnadjusted', headerName: 'Entity PMPM Unadj.', flex: 1, type: 'number', renderCell: params => valueFormatter('currency', params.value) },
    { field: 'networkPMPMUnadjusted', headerName: 'Network PMPM Unadj.', flex: 1, type: 'number', renderCell: params => valueFormatter('currency', params.value) },
    { field: 'networkPMPMRisk', headerName: 'Network PMPM Risk', flex: 1, type: 'number', renderCell: params => valueFormatter('currency', params.value) },
    { field: 'entityMemberMonths', headerName: 'Entity MM', flex: 1, type: 'number' },
    { field: 'networkMemberMonths', headerName: 'Network MM', flex: 1, type: 'number' },
    { field: 'entityEvents', headerName: 'Entity Events', flex: 1, type: 'number' },
    { field: 'entityEventsDiv1000Unadjusted', headerName: 'Ent. Events/1000 Unadj.', flex: 1, type: 'number' },
    { field: 'networkEventsDiv1000Unadjusted', headerName: 'Net. Events/1000 Unadj.', flex: 1, type: 'number' },
    { field: 'networkEventsDiv1000Adjusted', headerName: 'Net. Events/1000 Adj.', flex: 1, type: 'number', renderCell: params => params?.value ?? 'missing' },
    {
        field: 'diffNetworkEventsDiv1000Adjusted',
        headerName: 'Diff Net Events/1000 Adj',
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('percent', params.value)
    },
    {
        field: 'networkTCOCDivEntityEvents',
        headerName: 'Net TCOC / Ent Events',
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'networkTCOCDivEvents',
        headerName: 'Net TCOC / Events',
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('currency', params.value)
    },
    {
        field: 'diffNetworkEventsRisk',
        headerName: 'Difference to Network PMPM (Risk Adj. to Entity)',
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('percent', params.value)
    },
    {
        field: 'entityTCOCDivEvents',
        headerName: 'Entity TCOC / Entity Events',
        flex: 1,
        type: 'number',
        renderCell: params => valueFormatter('percent', params.value)
    }
]

const slotProps: any = {
    loadingOverlay: {
        variant: 'skeleton',
        noRowsVariant: 'skeleton'
    }
}

const sx = {
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: theme => theme.palette.background.paper
    },
    'borderStyle': 'none',
    '&.MuiDataGrid-root': {
        paddingTop: 3,
        borderRadius: 2.5
    }, '& .MuiDataGrid-cell:focus': {
        outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    }
}

const PAGE_SIZE = 20

const CostUtilizationView = ({ contractId, filterStartDate, filterEndDate, changeViewFunction, updateDataFlag, setExportMenu }: P) => {
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZE })
    const [rowCountState, setRowCountState] = useState(0)
    const [offset, setOffset] = useState(0)
    const paginationMetaRef = useRef<GridPaginationMeta>(undefined)

    const { response: responseItems, status: responseStatus } = useQuery<IPaginationQueryResponse<ICostUtilization>>({
        queryKey: buildQueryKey('contractCostUtilization', contractId, filterStartDate, filterEndDate, paginationModel.page === 0 ? 'first' : paginationModel.page),
        queryFn: async () => {
            const result = await ApiClient.getContractCostUtilizationByPeriod(contractId, filterStartDate, filterEndDate, PAGE_SIZE, offset)
            if (result?.items?.length > 0) setOffset(prev => prev + PAGE_SIZE)
            return result
        },
        cache: true
    })

    const paginationMeta = useMemo(() => {
        if (responseItems?.hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== responseItems?.hasNextPage) {
            paginationMetaRef.current = { hasNextPage: responseItems?.hasNextPage }
        }
        return paginationMetaRef.current
    }, [responseItems?.hasNextPage])

    const rows = useMemo(() => responseItems?.items ?? [], [responseItems?.items])

    useEffect(() => {
        if (!paginationMeta?.hasNextPage) {
            setRowCountState(paginationModel.page * PAGE_SIZE + (responseItems?.items?.length ?? 0))
        } else {
            setRowCountState(-1)
        }
    }, [paginationMeta?.hasNextPage, paginationModel.page, responseItems?.items])

    useEffect(() => {
        setOffset(0)
        setPaginationModel({ page: 0, pageSize: PAGE_SIZE })
    }, [updateDataFlag])

    const handleChangeView = () => {
        changeViewFunction('normal')
    }

    const handlePaginationModelChange = useCallback((newPaginationModel: GridPaginationModel) => {
        setOffset(newPaginationModel.page * PAGE_SIZE)
        setPaginationModel(newPaginationModel)
    }, [])

    if (responseStatus === 'error') snackbar.show({ severity: 'error', text: 'Error during data fetch, try refreshing the page' })

    return (
        <>
            <Tile.Container>
                <Tile.Column>
                    <SmartCard>
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <Stack>
                                <Typography variant='h6'>
                                    Cost utilization
                                </Typography>
                                <Typography variant='body2'>
                                    March
                                </Typography>
                            </Stack>
                            <IconButton
                                onClick={() => {
                                    handleChangeView()
                                    setExportMenu(['csv'])
                                }}
                                size='small'>
                                <CloseFullscreenRounded />
                            </IconButton>
                        </Stack>
                        <DataGrid
                            sx={sx}
                            loading={responseStatus === 'loading'}
                            columns={columns}
                            getRowId={row => row.uuid}
                            pageSizeOptions={[PAGE_SIZE]}
                            rows={rows}
                            rowCount={rowCountState}
                            paginationMode='server'
                            paginationMeta={paginationMeta}
                            paginationModel={paginationModel}
                            onPaginationModelChange={handlePaginationModelChange}
                            slotProps={slotProps}
                            disableRowSelectionOnClick
                            autosizeOnMount
                        />
                    </SmartCard>
                </Tile.Column>
            </Tile.Container>
        </>
    )
}

export default CostUtilizationView
